// /allergies — the entry surface for the round-4 #1 safety unblock.
//
// 8 of 20 personas (Tanya, Sean, Leona, Earl, Ba-Thu, Marisol, Doris,
// Mei) went hunting for a place to record their allergies on first
// open and came up empty. The cross-reactivity checker ships
// backend-correct but invisible without this page.
//
// Layout:
//   1. Quick-add chips for the 6 cross-react groups (penicillin, sulfa,
//      NSAID, ACE-inhibitor, statin, codeine/opioid) — tap and confirm.
//   2. List of recorded allergies with severity + remove.
//   3. Free-text "Other allergy" form for anything outside the 6 groups.
//
// Senior UX: large tap targets (56px chips), explicit "Add" confirm
// before save (no auto-add on chip tap), severity defaults to MODERATE,
// and a plain-English explanation of WHY each group matters.

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  type AllergyRow,
  type AllergySeverity,
  type CreateAllergyInput,
  useAllergiesApi,
} from '../api/allergies';
import { SiggyAvatar } from '../components/SiggyAvatar';
import { ApiError } from '../components/senior/ApiError';
import { SeniorButton } from '../components/senior/SeniorButton';
import { useSubjectName } from '../hooks/useSubjectName';
import { AppPage } from '../layouts/AppPage';

interface QuickGroup {
  id: string;
  label: string;
  whyItMatters: string;
  /** Example allergen name pre-filled when the user taps this chip. */
  defaultAllergen: string;
}

const QUICK_GROUPS: QuickGroup[] = [
  {
    id: 'penicillin',
    label: 'Penicillin family',
    whyItMatters:
      'Includes amoxicillin, ampicillin, Augmentin. Reactions to one usually mean reactions to others.',
    defaultAllergen: 'Penicillin',
  },
  {
    id: 'sulfa',
    label: 'Sulfa antibiotics',
    whyItMatters:
      'Most often Bactrim or Septra. Can cause rash, hives, or rarely severe reactions.',
    defaultAllergen: 'Sulfa',
  },
  {
    id: 'nsaid',
    label: 'NSAIDs / aspirin',
    whyItMatters:
      'Aspirin, ibuprofen, naproxen, Aleve, Advil. If one caused hives, asthma, or breathing trouble, others usually will too.',
    defaultAllergen: 'NSAIDs',
  },
  {
    id: 'ace-inhibitor',
    label: 'ACE inhibitors (angioedema)',
    whyItMatters:
      'Lisinopril, enalapril, ramipril. Pick this if you ever had face, tongue, or throat swelling on a blood-pressure pill.',
    defaultAllergen: 'ACE inhibitor (angioedema)',
  },
  {
    id: 'statin',
    label: 'Statins',
    whyItMatters:
      'Atorvastatin, simvastatin, rosuvastatin. Pick this if a statin caused severe muscle problems.',
    defaultAllergen: 'Statin',
  },
  {
    id: 'opioid-codeine',
    label: 'Codeine / opioids',
    whyItMatters:
      'Codeine, morphine, oxycodone, hydrocodone. True allergy is rare; pick this only if a doctor diagnosed one.',
    defaultAllergen: 'Codeine',
  },
];

const SEVERITY_LABEL: Record<AllergySeverity, string> = {
  MILD: 'Mild — minor reaction',
  MODERATE: 'Moderate — significant reaction',
  SEVERE: 'Severe — serious reaction',
  ANAPHYLAXIS: 'Anaphylaxis — emergency reaction',
};

interface DraftAllergy {
  allergen: string;
  allergenClass: string;
  reaction: string;
  severity: AllergySeverity;
}

const EMPTY_DRAFT: DraftAllergy = {
  allergen: '',
  allergenClass: '',
  reaction: '',
  severity: 'MODERATE',
};

export function Allergies(): JSX.Element {
  const api = useAllergiesApi();
  const subject = useSubjectName();
  const [allergies, setAllergies] = useState<AllergyRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState<DraftAllergy>(EMPTY_DRAFT);

  const reload = async (): Promise<void> => {
    setError(null);
    setLoading(true);
    try {
      const items = await api.list();
      setAllergies(items);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: mount-only fetch
  useEffect(() => {
    void reload();
  }, []);

  const onQuickAdd = (group: QuickGroup): void => {
    setDraft({
      allergen: group.defaultAllergen,
      allergenClass: group.id,
      reaction: '',
      severity: 'MODERATE',
    });
  };

  const onSave = async (): Promise<void> => {
    if (!draft.allergen.trim() || busy) return;
    setBusy(true);
    setError(null);
    try {
      const input: CreateAllergyInput = {
        allergen: draft.allergen.trim(),
        severity: draft.severity,
      };
      if (draft.allergenClass) input.allergenClass = draft.allergenClass;
      if (draft.reaction.trim()) input.reaction = draft.reaction.trim();
      await api.create(input);
      setDraft(EMPTY_DRAFT);
      await reload();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  const onRemove = async (id: string): Promise<void> => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await api.remove(id);
      await reload();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  const title = subject.possessive
    ? `${subject.possessive} allergies`
    : 'Allergies and sensitivities';

  return (
    <AppPage title={title} back={{ to: '/account', label: 'Account' }}>
      <section className="food-intro">
        <SiggyAvatar size={48} />
        <div>
          <p className="food-intro__eyebrow">Why this matters</p>
          <p className="food-intro__text">
            Tell us about any medicine allergies. We use this to warn you the moment a new
            prescription would cross-react with something you have already reacted to. The list
            below stays private to you and the people you share with.
          </p>
        </div>
      </section>

      {loading ? <p style={{ padding: 'var(--space-md)' }}>Loading...</p> : null}
      {error ? <ApiError error={error} onRetry={() => void reload()} /> : null}

      {!loading && allergies.length > 0 ? (
        <section className="account-section">
          <h3 className="account-section__title">On file</h3>
          <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: 8 }}>
            {allergies.map((a) => (
              <li
                key={a.id}
                style={{
                  padding: 12,
                  border: '1px solid var(--color-border)',
                  borderRadius: 'var(--radius-md)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 12,
                }}
              >
                <div>
                  <p style={{ margin: 0, fontSize: 17, fontWeight: 700 }}>{a.allergen}</p>
                  <p
                    style={{
                      margin: '2px 0 0',
                      fontSize: 14,
                      color: 'var(--color-text-secondary)',
                    }}
                  >
                    {SEVERITY_LABEL[a.severity]}
                    {a.reaction ? ` · ${a.reaction}` : ''}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => void onRemove(a.id)}
                  disabled={busy}
                  aria-label={`Remove ${a.allergen}`}
                  style={{
                    minHeight: 44,
                    minWidth: 80,
                    padding: '0 16px',
                    background: 'transparent',
                    color: 'var(--color-danger)',
                    border: '1.5px solid var(--color-border)',
                    borderRadius: 8,
                    fontSize: 15,
                    fontWeight: 600,
                    cursor: busy ? 'not-allowed' : 'pointer',
                  }}
                >
                  Remove
                </button>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      <section className="account-section">
        <h3 className="account-section__title">Add an allergy</h3>
        <p style={{ margin: '0 0 12px', fontSize: 15, color: 'var(--color-text-secondary)' }}>
          Tap a common group below, or describe a different allergy.
        </p>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
          {QUICK_GROUPS.map((g) => (
            <button
              key={g.id}
              type="button"
              onClick={() => onQuickAdd(g)}
              aria-pressed={draft.allergenClass === g.id}
              className={`symptom-pill${draft.allergenClass === g.id ? ' is-active' : ''}`}
              style={{ minHeight: 56, minWidth: 140, textAlign: 'left' }}
            >
              {g.label}
            </button>
          ))}
        </div>

        {draft.allergenClass ? (
          <p
            style={{
              margin: '0 0 12px',
              padding: 12,
              background: 'var(--color-surface-secondary)',
              borderRadius: 'var(--radius-md)',
              fontSize: 15,
            }}
          >
            {QUICK_GROUPS.find((g) => g.id === draft.allergenClass)?.whyItMatters}
          </p>
        ) : null}

        <label style={{ display: 'block', marginBottom: 12 }}>
          <span style={{ display: 'block', fontWeight: 600, marginBottom: 6 }}>
            Name of the allergy
          </span>
          <input
            type="text"
            value={draft.allergen}
            onChange={(e) => setDraft({ ...draft, allergen: e.target.value })}
            placeholder="e.g. Penicillin, Shellfish"
            style={{
              width: '100%',
              padding: '12px 14px',
              fontSize: 17,
              border: '1.5px solid var(--color-border)',
              borderRadius: 'var(--radius-md)',
              boxSizing: 'border-box',
            }}
          />
        </label>

        <label style={{ display: 'block', marginBottom: 12 }}>
          <span style={{ display: 'block', fontWeight: 600, marginBottom: 6 }}>
            What happened (optional)
          </span>
          <input
            type="text"
            value={draft.reaction}
            onChange={(e) => setDraft({ ...draft, reaction: e.target.value })}
            placeholder="e.g. Hives, throat swelling"
            style={{
              width: '100%',
              padding: '12px 14px',
              fontSize: 17,
              border: '1.5px solid var(--color-border)',
              borderRadius: 'var(--radius-md)',
              boxSizing: 'border-box',
            }}
          />
        </label>

        <fieldset style={{ border: 0, padding: 0, margin: '0 0 16px' }}>
          <legend style={{ fontWeight: 600, marginBottom: 8 }}>How serious was it</legend>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {(['MILD', 'MODERATE', 'SEVERE', 'ANAPHYLAXIS'] as AllergySeverity[]).map((s) => (
              <label
                key={s}
                style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 16 }}
              >
                <input
                  type="radio"
                  name="severity"
                  value={s}
                  checked={draft.severity === s}
                  onChange={() => setDraft({ ...draft, severity: s })}
                />
                <span>{SEVERITY_LABEL[s]}</span>
              </label>
            ))}
          </div>
        </fieldset>

        <SeniorButton
          variant="primary"
          onClick={() => void onSave()}
          disabled={!draft.allergen.trim() || busy}
        >
          {busy ? 'Saving...' : 'Add allergy'}
        </SeniorButton>
      </section>

      <section className="account-section">
        <p style={{ margin: 0, fontSize: 14, color: 'var(--color-text-secondary)' }}>
          Allergies you add here are checked against every new medicine you add. If something looks
          risky, we'll flash a CRITICAL alert before saving.{' '}
          <Link to="/medications" className="account-link">
            Back to your medicines.
          </Link>
        </p>
      </section>
    </AppPage>
  );
}
